import React, { useEffect, useState, useMemo, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import { authHeaders } from '../../utils/authFetch'

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    dark: "#F9FAFB",
    amber: "#D97706",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
}

const BRANCH_COLORS = [C.amber, C.teal, C.blue, C.coral, C.green, C.purple]
const STAFF_PAGE_SIZE = 10

// ── Shared UI ──────────────────────────────────────────────────────────────
const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle, className = "mb-5" }) => (
    <div className={className}>
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold leading-none" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const StatusChip = ({ status }) => {
    const cfg = {
        present: { label: 'Present', color: C.green },
        late: { label: 'Late', color: C.amber },
        absent: { label: 'Absent', color: C.coral },
        on_leave: { label: 'On Leave', color: C.purple },
        waiting: { label: 'Waiting', color: C.muted },
        not_yet: { label: 'Not Yet', color: C.muted },
        day_off: { label: 'Day Off', color: C.blue },
    }[status] ?? { label: status, color: C.muted }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: cfg.color + '22', color: cfg.color }}>
            {cfg.label}
        </span>
    )
}

const OT_THRESHOLD_MS = 15 * 60 * 1000 // 15 min grace before flagging OT/UT

const getOvertimeStatus = (record) => {
    if (!record?.clock_in || !record?.clock_out || !record?.scheduled_in || !record?.scheduled_out) return null
    const actualMs = new Date(record.clock_out) - new Date(record.clock_in)
    const scheduledMs = new Date(record.scheduled_out) - new Date(record.scheduled_in)
    const diff = actualMs - scheduledMs
    if (diff > OT_THRESHOLD_MS) return { type: 'overtime', diffMs: diff }
    if (diff < -OT_THRESHOLD_MS) return { type: 'undertime', diffMs: Math.abs(diff) }
    return null
}

const OvertimeChip = ({ record }) => {
    const ot = getOvertimeStatus(record)
    if (!ot) return <span className="text-[10px]" style={{ color: C.muted }}>—</span>
    const mins = Math.round(ot.diffMs / 60000)
    const h = Math.floor(mins / 60), m = mins % 60
    const label = h > 0 ? `${h}h ${m}m` : `${m}m`
    const isOver = ot.type === 'overtime'
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: (isOver ? C.blue : C.coral) + '22', color: isOver ? C.blue : C.coral }}>
            {isOver ? `+${label} OT` : `-${label} UT`}
        </span>
    )
}

// ── Photo Modal ────────────────────────────────────────────────────────────
function PhotoModal({ url, label, onClose }) {
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)' }}
            onClick={onClose}>
            <div className="relative flex flex-col items-center gap-3"
                onClick={e => e.stopPropagation()}>
                <button onClick={onClose}
                    className="absolute -top-10 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
                <img src={url} alt={label}
                    className="rounded-2xl shadow-2xl object-contain"
                    style={{ maxWidth: '90vw', maxHeight: '80vh' }} />
                <span className="text-sm font-semibold text-white/70">{label}</span>
            </div>
        </div>
    )
}

// ── Helpers ────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0')

// Local (browser/PH) calendar date — avoids the UTC-offset bug where
// toISOString() rolls back to "yesterday" during PH early-morning hours
const getLocalDateStr = (d = new Date()) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const fmtDuration = (ms) => {
    if (!ms || ms <= 0) return '—'
    const totalMins = Math.floor(ms / 60000)
    const h = Math.floor(totalMins / 60)
    const m = totalMins % 60
    return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`
}

const fmtCurrency = (n) =>
    n == null ? '—' : `₱${Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`

const presetStart = (range) => {
    if (range === 'all') return null
    const days = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000)
}

const shortBranchName = (name) =>
    name?.replace(/Coffee Gus[·•]?to\s*/i, '') ?? name ?? '—'

// ── Today-status derivation (mirrors ShiftAttendance) ─────────────────────
const ENABLE_BEFORE_MS = 60 * 60 * 1000

const deriveTodayStatus = (record, now, schedule) => {
    if (record?.clock_in) return null

    // Check day off first
    const dateStr = record?.date ?? new Date().toISOString().slice(0, 10)
    if (isDayOff(dateStr, schedule)) return 'day_off'

    const scheduled_in = record?.scheduled_in ? new Date(record.scheduled_in) : null
    const scheduled_out = record?.scheduled_out ? new Date(record.scheduled_out) : null

    if (!scheduled_in) return 'waiting'
    if (now < new Date(scheduled_in.getTime() - ENABLE_BEFORE_MS)) return 'not_yet'
    if (now < scheduled_in) return 'waiting'
    if (now >= scheduled_in && (!scheduled_out || now < scheduled_out)) return 'late'
    if (scheduled_out && now >= scheduled_out) return 'absent'
    return 'waiting'
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const isDayOff = (dateStr, schedule) => {
    if (!schedule?.work_days?.length) return false
    const dayKey = DAY_KEYS[new Date(dateStr + 'T00:00:00').getDay()]
    return !schedule.work_days.includes(dayKey)
}

// ── Range Selector ─────────────────────────────────────────────────────────
const RangeSelector = ({ range, setRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
    const options = [
        { label: 'All Time', value: 'all' },
        { label: '7 days', value: '7d' },
        { label: '30 days', value: '30d' },
        { label: '90 days', value: '90d' },
        { label: '1 year', value: '1y' },
        { label: 'Custom', value: 'custom' },
    ]

    const hasCustom = customStart && customEnd

    const handlePreset = (value) => {
        setRange(value)
        if (value !== 'custom') {
            setCustomStart('')
            setCustomEnd('')
        }
    }

    return (
        <div className="flex flex-col gap-2 items-end w-full">
            <div className="flex gap-1.5 overflow-x-auto pb-1 max-w-full justify-end" style={{ scrollbarWidth: 'thin' }}>
                {options.map(o => (
                    <button key={o.value} onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors flex-shrink-0 whitespace-nowrap"
                        style={{
                            background: range === o.value && !hasCustom ? C.amber : C.card,
                            borderColor: range === o.value && !hasCustom ? C.amber : C.border,
                            color: range === o.value && !hasCustom ? '#fff' : C.textSoft,
                            cursor: 'pointer',
                        }}>
                        {o.label}
                    </button>
                ))}
            </div>
            {(range === 'custom' || hasCustom) && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1.5 border-t border-dashed"
                    style={{ borderColor: C.border }}>
                    <span className="text-[11px]" style={{ color: C.muted }}>From:</span>
                    <input type="date" value={customStart}
                        onChange={e => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border, color: C.text, background: C.card }} />
                    <span className="text-[11px]" style={{ color: C.muted }}>To:</span>
                    <input type="date" value={customEnd}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border, color: C.text, background: C.card }} />
                    {hasCustom && (
                        <button onClick={() => { setCustomStart(''); setCustomEnd(''); setRange('all') }}
                            className="text-[11px] px-2 py-1 rounded-full border"
                            style={{ borderColor: C.border, color: C.muted, cursor: 'pointer', background: C.card }}>
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

// ── Branch Filter ──────────────────────────────────────────────────────────
const BranchFilter = ({ branches, selectedBranch, setSelectedBranch }) => (
    <div className="flex gap-1.5 items-center overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
        <span className="text-[11px] uppercase tracking-wider mr-1 flex-shrink-0" style={{ color: C.muted }}>Branch</span>
        <button onClick={() => setSelectedBranch(null)}
            className="px-3 py-1 rounded-full text-[11px] border transition-colors flex-shrink-0 whitespace-nowrap"
            style={{
                background: selectedBranch === null ? C.teal : C.card,
                borderColor: selectedBranch === null ? C.teal : C.border,
                color: selectedBranch === null ? '#fff' : C.textSoft,
                cursor: 'pointer',
            }}>
            All Branches
        </button>
        {(branches ?? []).map((b, i) => (
            <button key={b.branch_id} onClick={() => setSelectedBranch(b.branch_id)}
                className="px-3 py-1 rounded-full text-[11px] border transition-colors flex-shrink-0 whitespace-nowrap"
                style={{
                    background: selectedBranch === b.branch_id ? BRANCH_COLORS[i % BRANCH_COLORS.length] : C.card,
                    borderColor: selectedBranch === b.branch_id ? BRANCH_COLORS[i % BRANCH_COLORS.length] : C.border,
                    color: selectedBranch === b.branch_id ? '#fff' : C.textSoft,
                    cursor: 'pointer',
                }}>
                {shortBranchName(b.name)}
            </button>
        ))}
    </div>
)

// ── Role filter dropdown (radio buttons) ───────────────────────────────────
const RoleFilterDropdown = ({ roles, selectedRole, setSelectedRole }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const options = [{ value: 'all', label: 'All Roles' }, ...roles.map(r => ({ value: r, label: r }))]
    const currentLabel = options.find(o => o.value === selectedRole)?.label ?? 'All Roles'

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="px-3 py-2 rounded-xl border text-sm outline-none flex items-center gap-2"
                style={{ borderColor: C.border, color: C.text, background: C.dark, cursor: 'pointer' }}>
                {currentLabel}
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                    className="transition-transform"
                    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <path fill="currentColor" d="M7 10l5 5l5-5z" />
                </svg>
            </button>
            {open && (
                <div className="absolute z-20 mt-1 w-44 rounded-xl border shadow-lg p-2 flex flex-col gap-1"
                    style={{ borderColor: C.border, background: C.card }}>
                    {options.map(o => (
                        <label key={o.value}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50"
                            style={{ fontSize: 13, color: C.text }}>
                            <input
                                type="radio"
                                name="role-filter"
                                checked={selectedRole === o.value}
                                onChange={() => { setSelectedRole(o.value); setOpen(false) }}
                                style={{ accentColor: C.amber }}
                            />
                            {o.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    )
}

// ── Date helpers ───────────────────────────────────────────────────────────
const useDateBounds = (range, customStart, customEnd) =>
    useMemo(() => {
        const hasCustom = customStart && customEnd
        if (hasCustom) return {
            startBound: new Date(customStart),
            endBound: new Date(customEnd + 'T23:59:59'),
        }
        return { startBound: presetStart(range), endBound: null }
    }, [range, customStart, customEnd])

const filterByDate = (records, dateField, startBound, endBound) => {
    if (!startBound && !endBound) return records
    return records.filter(r => {
        const d = new Date(r[dateField])
        if (startBound && d < startBound) return false
        if (endBound && d > endBound) return false
        return true
    })
}

const periodLabel = (startBound, endBound) => {
    if (!startBound) return 'All Time'
    const fmt = (d) => d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
    return `${fmt(startBound)} — ${endBound ? fmt(endBound) : 'Today'}`
}

const buildBaristaOrderMaps = (baristaIds, filteredOrders) => {
    const ordersMap = {}, posMap = {}
    baristaIds.forEach(id => {
        ordersMap[id] = { count: 0, revenue: 0 }
        posMap[id] = { count: 0, revenue: 0 }
    })
    filteredOrders.forEach(o => {
        if (o.order_completed_by && ordersMap[o.order_completed_by]) {
            ordersMap[o.order_completed_by].count += 1
            ordersMap[o.order_completed_by].revenue += Number(o.final_amount ?? 0)
        }
        if (o.pos_processed_by && posMap[o.pos_processed_by]) {
            posMap[o.pos_processed_by].count += 1
            posMap[o.pos_processed_by].revenue += Number(o.final_amount ?? 0)
        }
    })
    return { prepMap: ordersMap, posMap }
}

const buildOrderMaps = (staffIds, filteredOrders) => {
    const oMap = {}, pMap = {}
    staffIds.forEach(id => {
        oMap[id] = { count: 0, revenue: 0 }
        pMap[id] = { count: 0, revenue: 0 }
    })
    filteredOrders.forEach(o => {
        if (!o.staff_id) return
        if (o.order_source === 'pos') {
            pMap[o.staff_id].count += 1
            pMap[o.staff_id].revenue += Number(o.final_amount ?? 0)
        } else {
            oMap[o.staff_id].count += 1
            oMap[o.staff_id].revenue += Number(o.final_amount ?? 0)
        }
    })
    return { oMap, pMap }
}

// ── Avatar ─────────────────────────────────────────────────────────────────
const Avatar = ({ firstName, lastName, profilePic, size = 36 }) => {
    const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
    if (profilePic) return (
        <img src={profilePic} alt={initials} className="rounded-full object-cover flex-shrink-0"
            style={{ width: size, height: size }} />
    )
    const colors = [C.amber, C.teal, C.blue, C.purple, C.green, C.coral]
    const idx = (firstName?.charCodeAt(0) ?? 0) % colors.length
    return (
        <div className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white"
            style={{ width: size, height: size, background: colors[idx], fontSize: size * 0.35 }}>
            {initials}
        </div>
    )
}

// ── Network-wide KPI summary ───────────────────────────────────────────────
function NetworkSummaryBar({ staffList, baristaList, branchMap, rawAttendanceMap, rawBaristaAttendanceMap, ordersMap, baristaOrdersMap, posMap, baristaPosMap, processedMap, startBound, endBound, selectedBranchName }) {
    const today = getLocalDateStr()

    const totalStaff = staffList.length + baristaList.length

    const presentToday =
        staffList.filter(s =>
            rawAttendanceMap[s.id]?.some(r => r.date === today && (r.status === 'present' || r.status === 'late'))
        ).length +
        baristaList.filter(b =>
            rawBaristaAttendanceMap[b.barista_id]?.some(r => r.date === today && (r.status === 'present' || r.status === 'late'))
        ).length

    const totalOrders =
        staffList.reduce((sum, s) => sum + (processedMap?.[s.id]?.orderCount ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaOrdersMap?.[b.barista_id]?.count ?? 0), 0)

    const totalRevenue =
        staffList.reduce((sum, s) => sum + (processedMap?.[s.id]?.revenue ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaOrdersMap?.[b.barista_id]?.revenue ?? 0), 0)

    const totalPOS =
        staffList.reduce((sum, s) => sum + (posMap[s.id]?.count ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaPosMap?.[b.barista_id]?.count ?? 0), 0)

    const totalPOSRev =
        staffList.reduce((sum, s) => sum + (posMap[s.id]?.revenue ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaPosMap?.[b.barista_id]?.revenue ?? 0), 0)

    const branchCount = Object.keys(branchMap).length

    return (
        <Card>
            <SectionTitle
                title={selectedBranchName ? `${selectedBranchName} Overview` : "Network Overview"}
                subtitle={`${selectedBranchName ?? 'All Branches'} · ${periodLabel(startBound, endBound)}`}
                className="mb-0"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-4">
                <KPI label="Branches" value={selectedBranchName ? 1 : branchCount} color={C.teal} />
                <KPI label="Total Staff" value={totalStaff} color={C.blue} />
                <KPI label="Present Today" value={presentToday} color={C.green} sub={`of ${totalStaff}`} />
                <KPI label="Orders Managed" value={totalOrders} color={C.amber} sub="all staff" />
                <KPI label="Order Revenue" value={fmtCurrency(totalRevenue)} color={C.teal} sub="all staff" />
                <KPI label="POS Transactions" value={totalPOS} color={C.purple} sub={fmtCurrency(totalPOSRev)} />
            </div>
        </Card>
    )
}

function BranchBreakdownTable({ branches, staffByBranch, baristaByBranch, rawAttendanceMap, rawBaristaAttendanceMap, processedMap, baristaOrdersMap }) {
    const today = getLocalDateStr()

    const rows = useMemo(() => branches.map((b, i) => {
        const staff = staffByBranch[b.branch_id] ?? []
        const baristas = baristaByBranch[b.branch_id] ?? []
        const headcount = staff.length + baristas.length

        // Present today uses raw (unfiltered) attendance, same as Network Overview
        const present =
            staff.filter(s => rawAttendanceMap[s.id]?.some(r => r.date === today && (r.status === 'present' || r.status === 'late'))).length +
            baristas.filter(x => rawBaristaAttendanceMap[x.barista_id]?.some(r => r.date === today && (r.status === 'present' || r.status === 'late'))).length

        const orders =
            staff.reduce((sum, s) => sum + (processedMap?.[s.id]?.orderCount ?? 0), 0) +
            baristas.reduce((sum, x) => sum + (baristaOrdersMap?.[x.barista_id]?.count ?? 0), 0)

        const revenue =
            staff.reduce((sum, s) => sum + (processedMap?.[s.id]?.revenue ?? 0), 0) +
            baristas.reduce((sum, x) => sum + (baristaOrdersMap?.[x.barista_id]?.revenue ?? 0), 0)

        return {
            branch: b,
            color: BRANCH_COLORS[i % BRANCH_COLORS.length],
            headcount,
            present,
            pct: headcount ? Math.round((present / headcount) * 100) : 0,
            orders,
            revenue,
            avg: orders ? revenue / orders : null,
        }
    }), [branches, staffByBranch, baristaByBranch, rawAttendanceMap, rawBaristaAttendanceMap, processedMap, baristaOrdersMap, today])

    const totals = useMemo(() => {
        const t = rows.reduce((a, r) => ({
            headcount: a.headcount + r.headcount,
            present: a.present + r.present,
            orders: a.orders + r.orders,
            revenue: a.revenue + r.revenue,
        }), { headcount: 0, present: 0, orders: 0, revenue: 0 })
        return {
            ...t,
            pct: t.headcount ? Math.round((t.present / t.headcount) * 100) : 0,
            avg: t.orders ? t.revenue / t.orders : null,
        }
    }, [rows])

    const th = 'py-2.5 px-4 font-semibold uppercase tracking-wide whitespace-nowrap'

    return (
        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: C.border }}>
            <table className="w-full text-xs border-collapse" style={{ minWidth: 720 }}>
                <thead>
                    <tr style={{ background: C.dark }}>
                        <th className={`${th} text-left`} style={{ color: C.muted, fontSize: 10 }}>Branch</th>
                        <th className={`${th} text-right`} style={{ color: C.muted, fontSize: 10 }}>Staff</th>
                        <th className={`${th} text-right`} style={{ color: C.muted, fontSize: 10 }}>Present Today</th>
                        <th className={`${th} text-left`} style={{ color: C.muted, fontSize: 10, minWidth: 150 }}>Presence</th>
                        <th className={`${th} text-right`} style={{ color: C.muted, fontSize: 10 }}>Orders</th>
                        <th className={`${th} text-right`} style={{ color: C.muted, fontSize: 10 }}>Revenue</th>
                        <th className={`${th} text-right`} style={{ color: C.muted, fontSize: 10 }}>Avg Order</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.branch.branch_id} className="hover:bg-gray-50 transition-colors"
                            style={{ borderTop: `1px solid ${C.border}` }}>
                            <td className="py-3 px-4">
                                <div className="flex items-center gap-2.5">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color }} />
                                    <span className="font-semibold text-[12px]" style={{ color: C.text }}>
                                        {shortBranchName(r.branch.name)}
                                    </span>
                                </div>
                            </td>
                            <td className="py-3 px-4 text-right font-semibold" style={{ color: C.text }}>{r.headcount}</td>
                            <td className="py-3 px-4 text-right font-semibold" style={{ color: C.green }}>{r.present}</td>
                            <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                    <div className="h-1.5 rounded-full flex-1" style={{ background: C.border }}>
                                        <div className="h-1.5 rounded-full transition-all duration-500"
                                            style={{ width: `${r.pct}%`, background: r.color }} />
                                    </div>
                                    <span className="text-[11px] font-semibold w-9 text-right" style={{ color: C.textSoft }}>
                                        {r.pct}%
                                    </span>
                                </div>
                            </td>
                            <td className="py-3 px-4 text-right font-semibold" style={{ color: C.amber }}>{r.orders}</td>
                            <td className="py-3 px-4 text-right font-semibold whitespace-nowrap" style={{ color: C.teal }}>
                                {fmtCurrency(r.revenue)}
                            </td>
                            <td className="py-3 px-4 text-right whitespace-nowrap" style={{ color: C.textSoft }}>
                                {r.avg != null ? fmtCurrency(r.avg) : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr style={{ background: C.dark, borderTop: `2px solid ${C.border}` }}>
                        <td className="py-3 px-4 font-bold uppercase tracking-wide text-[11px]" style={{ color: C.text }}>
                            Total · {rows.length} branch{rows.length !== 1 ? 'es' : ''}
                        </td>
                        <td className="py-3 px-4 text-right font-bold" style={{ color: C.text }}>{totals.headcount}</td>
                        <td className="py-3 px-4 text-right font-bold" style={{ color: C.green }}>{totals.present}</td>
                        <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 rounded-full flex-1" style={{ background: C.border }}>
                                    <div className="h-1.5 rounded-full" style={{ width: `${totals.pct}%`, background: C.teal }} />
                                </div>
                                <span className="text-[11px] font-bold w-9 text-right" style={{ color: C.textSoft }}>{totals.pct}%</span>
                            </div>
                        </td>
                        <td className="py-3 px-4 text-right font-bold" style={{ color: C.amber }}>{totals.orders}</td>
                        <td className="py-3 px-4 text-right font-bold whitespace-nowrap" style={{ color: C.teal }}>{fmtCurrency(totals.revenue)}</td>
                        <td className="py-3 px-4 text-right font-bold whitespace-nowrap" style={{ color: C.textSoft }}>
                            {totals.avg != null ? fmtCurrency(totals.avg) : '—'}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

// ── Staff detail drawer ────────────────────────────────────────────────────
function StaffDrawer({ staff, attendance, orders, pos, processed, schedule, branchName, onClose, onLeaveDeleted, startBound, endBound, isBarista, onViewPhoto }) {
    const [showLeaveForm, setShowLeaveForm] = useState(false)
    const [leaveForm, setLeaveForm] = useState({ start_date: '', end_date: '', reason: '' })
    const [savingLeave, setSavingLeave] = useState(false)
    const [leaveError, setLeaveError] = useState(null)
    const [deletingLeaveId, setDeletingLeaveId] = useState(null)

    const windowAtt = useMemo(() =>
        filterByDate(attendance ?? [], 'date', startBound, endBound),
        [attendance, startBound, endBound])

    const present = windowAtt.filter(r => r.status === 'present').length
    const late = windowAtt.filter(r => r.status === 'late').length
    const absent = windowAtt.filter(r => r.status === 'absent').length
    const onLeave = windowAtt.filter(r => r.status === 'on_leave').length
    const totalMs = windowAtt
        .filter(r => r.clock_in && r.clock_out)
        .reduce((s, r) => s + (new Date(r.clock_out) - new Date(r.clock_in)), 0)
    const worked = present + late
    const rate = windowAtt.length ? Math.round((worked / windowAtt.length) * 100) : 0

    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

    const todayStr = getLocalDateStr()
    const sortedHistory = [...(attendance ?? [])]
        .filter(r => r.date <= todayStr)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10)


    const fmtTime = (iso) => {
        if (!iso) return '—'
        const d = new Date(iso)
        const h = d.getHours(), m = d.getMinutes()
        return `${pad(h % 12 || 12)}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`
    }
    const fmtDate = (iso) => {
        if (!iso) return '—'
        return new Date(iso).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    const handleSaveLeave = async (isBarista) => {
        if (!leaveForm.start_date || !leaveForm.end_date) return
        setSavingLeave(true)
        setLeaveError(null)
        try {
            const res = await fetch(`${BASE_URL}/api/staff-analytics/leave`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    staffId: staff.id,
                    branchId: staff.branch_id,
                    isBarista,
                    startDate: leaveForm.start_date,
                    endDate: leaveForm.end_date,
                    reason: leaveForm.reason,
                }),
            })
            const data = await res.json()
            if (!res.ok) {
                setLeaveError(data.error || 'Failed to save leave.')
                return
            }
            setLeaveForm({ start_date: '', end_date: '', reason: '' })
            setLeaveError(null)
            setShowLeaveForm(false)
        } catch (err) {
            setLeaveError(err.message)
        } finally {
            setSavingLeave(false)
        }
    }

    const handleDeleteLeave = async (record) => {
        setDeletingLeaveId(record.id)
        try {
            const res = await fetch(`${BASE_URL}/api/staff-analytics/leave/${record.id}`, { method: 'DELETE', headers: authHeaders() })
            if (!res.ok) {
                const body = await res.text()
                console.error('Delete leave failed:', res.status, body)
                return
            }
            onLeaveDeleted(record.id)
        } catch (err) {
            console.error('Delete leave error:', err)
        } finally {
            setDeletingLeaveId(null)
        }
    }


    const period = periodLabel(startBound, endBound)

    return (
        <div className="fixed inset-0 z-50 flex"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={onClose}>
            <div className="ml-auto w-full max-w-2xl h-full overflow-y-auto shadow-2xl bg-white flex flex-col"
                style={{ borderLeft: `1px solid ${C.border}` }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b"
                    style={{ borderColor: C.border }}>
                    <div className="flex items-center gap-3">
                        <Avatar firstName={staff.first_name} lastName={staff.last_name}
                            profilePic={staff.profile_picture_url} size={42} />
                        <div>
                            <p className="m-0 font-bold text-base" style={{ color: C.text }}>
                                {isBarista ? `${staff.first_name} ${staff.last_name}` : shortBranchName(branchName)}
                            </p>
                            {(isBarista ? staff.email : staff.company_email) && (
                                <p className="m-0 text-[11px] mt-0.5" style={{ color: C.textSoft }}>
                                    {isBarista ? staff.email : staff.company_email}
                                </p>
                            )}
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <p className="m-0 text-xs" style={{ color: C.muted }}>
                                    {staff.role} · {staff.employment_type}
                                </p>
                                {isBarista && (
                                    /* Branch badge — baristas only, admins already show branch as their name */
                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                        style={{ background: C.teal + '22', color: C.teal }}>
                                        {shortBranchName(branchName)}
                                    </span>
                                )}
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                    style={{ background: C.amber + '22', color: C.amber }}>
                                    {period}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                            fill="none" stroke={C.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-5">
                    {/* Attendance KPIs */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            Attendance — {period}
                        </p>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                            <KPI label="Present" value={present} color={C.green} sub="on time" />
                            <KPI label="Late" value={late} color={C.amber} sub="arrived late" />
                            <KPI label="Absent" value={absent} color={C.coral} sub="no-shows" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <KPI label="On Leave" value={onLeave} color={C.purple} />
                            <KPI label="Total Hours" value={fmtDuration(totalMs)} color={C.blue} />
                            <KPI label="Att. Rate" value={`${rate}%`}
                                color={rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral} />
                        </div>
                        <div className="mt-3 rounded-xl p-3 border" style={{ borderColor: C.border, background: C.dark }}>
                            <div className="flex justify-between mb-1.5">
                                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted }}>
                                    Attendance Rate
                                </span>
                                <span className="text-xs font-bold"
                                    style={{ color: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }}>
                                    {rate}%
                                </span>
                            </div>
                            <div className="h-2 rounded-full" style={{ background: C.border }}>
                                <div className="h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${rate}%`, background: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }} />
                            </div>
                        </div>
                    </div>

                    {/*
                    Leave Scheduling — removed from Super Admin view.
                    Branch Admins manage their own staff's leave; this whole
                    block (form, save/delete handlers, upcoming-leaves list)
                    is commented out here so it never renders on this page.

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-bold uppercase tracking-widest m-0" style={{ color: C.muted }}>
                                Schedule Leave
                            </p>
                            <button
                                onClick={() => { setShowLeaveForm(v => !v); setLeaveError(null) }}
                                className="text-[11px] px-3 py-1 rounded-full border font-semibold transition-colors"
                                style={{
                                    borderColor: showLeaveForm ? C.amber : C.border,
                                    color: showLeaveForm ? C.amber : C.textSoft,
                                    background: C.card,
                                    cursor: 'pointer',
                                }}>
                                {showLeaveForm ? '✕ Cancel' : '+ Add Leave'}
                            </button>
                        </div>
                        {showLeaveForm && (
                            <div className="rounded-xl border p-4 flex flex-col gap-3"
                                style={{ borderColor: C.border, background: C.dark }}>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[11px] font-semibold mb-1" style={{ color: C.muted }}>Start Date</label>
                                        <input type="date" value={leaveForm.start_date}
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={e => setLeaveForm(f => ({ ...f, start_date: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg border text-xs outline-none"
                                            style={{ borderColor: C.border, color: C.text, background: C.card }} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold mb-1" style={{ color: C.muted }}>End Date</label>
                                        <input type="date" value={leaveForm.end_date}
                                            min={leaveForm.start_date}
                                            onChange={e => setLeaveForm(f => ({ ...f, end_date: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg border text-xs outline-none"
                                            style={{ borderColor: C.border, color: C.text, background: C.card }} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold mb-1" style={{ color: C.muted }}>Reason (optional)</label>
                                    <input type="text" value={leaveForm.reason}
                                        placeholder="e.g. Sick leave, vacation…"
                                        onChange={e => setLeaveForm(f => ({ ...f, reason: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg border text-xs outline-none"
                                        style={{ borderColor: C.border, color: C.text, background: C.card }} />
                                </div>
                                <div className="rounded-lg px-3 py-2 text-[11px]"
                                    style={{ background: C.purple + '12', color: C.purple }}>
                                    This will mark each day in the selected range as <strong>On Leave</strong> in the attendance records. Existing clock-in records will be overwritten.
                                </div>
                                {leaveError && (
                                    <div className="rounded-lg px-3 py-2 text-[11px]"
                                        style={{ background: C.coral + '12', color: C.coral }}>
                                        ⚠ {leaveError}
                                    </div>
                                )}
                                <button onClick={() => handleSaveLeave(isBarista)}
                                    disabled={savingLeave || !leaveForm.start_date || !leaveForm.end_date}
                                    className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-opacity"
                                    style={{ background: C.purple, cursor: 'pointer' }}>
                                    {savingLeave ? 'Saving…' : 'Confirm Leave'}
                                </button>
                            </div>
                        )}

                        {(() => {
                            const today = new Date().toISOString().slice(0, 10)
                            const upcoming = (attendance ?? [])
                                .filter(r => r.status === 'on_leave' && r.date >= today)
                                .sort((a, b) => a.date.localeCompare(b.date))
                            if (upcoming.length === 0) return null
                            return (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                                        Upcoming Leaves ({upcoming.length})
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        {upcoming.map(r => (
                                            <div key={r.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border"
                                                style={{ borderColor: C.border, background: C.purple + '08' }}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                                        style={{ background: C.purple + '22', color: C.purple }}>
                                                        On Leave
                                                    </span>
                                                    <span className="text-xs font-medium" style={{ color: C.text }}>
                                                        {fmtDate(r.date)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {r.notes && (
                                                        <span className="text-[11px]" style={{ color: C.textSoft }}>{r.notes}</span>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteLeave(r)}
                                                        disabled={deletingLeaveId === r.id}
                                                        className="text-[10px] px-2 py-0.5 rounded-full border font-semibold transition-colors disabled:opacity-40"
                                                        style={{
                                                            borderColor: C.coral,
                                                            color: C.coral,
                                                            background: C.coral + '10',
                                                            cursor: 'pointer',
                                                        }}>
                                                        {deletingLeaveId === r.id ? '…' : '✕ Remove'}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        })()}
                    </div>
                    */}

                    {/* Order KPIs */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            Orders Managed — {period}
                        </p>
                        {isBarista ? (
                            <div className="grid grid-cols-2 gap-2">
                                <KPI label="Orders Completed" value={orders?.count ?? 0} color={C.teal} sub="via order_completed_by" />
                                <KPI label="Revenue Handled" value={fmtCurrency(orders?.revenue ?? 0)} color={C.blue} sub="from completed orders" />
                                <KPI label="Avg Order"
                                    value={(orders?.count ?? 0) > 0 ? fmtCurrency((orders?.revenue ?? 0) / orders.count) : '—'}
                                    color={C.amber} sub="per order" />
                                <KPI label="POS Handled" value={pos?.count ?? 0} color={C.purple} sub="via pos_processed_by" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                <KPI label="Orders Processed" value={processed?.orderCount ?? 0} color={C.teal} sub="unique orders" />
                                <KPI label="Items Prepared" value={processed?.count ?? 0} color={C.green} sub="items marked ready" />
                                <KPI label="Revenue Handled" value={fmtCurrency(processed?.revenue ?? 0)} color={C.blue} sub="from those orders" />
                                <KPI label="Avg Order"
                                    value={processed?.orderCount ? fmtCurrency((processed?.revenue ?? 0) / processed.orderCount) : '—'}
                                    color={C.amber} sub="per order" />
                            </div>
                        )}
                    </div>

                    {/* POS KPIs */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            POS Transactions — {period}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                            <KPI label="POS Transactions" value={pos?.count ?? 0} color={C.purple} />
                            <KPI label="POS Revenue" value={fmtCurrency(pos?.revenue ?? 0)} color={C.green} />
                            <KPI label="Avg POS"
                                value={pos?.count ? fmtCurrency((pos.revenue ?? 0) / pos.count) : '—'}
                                color={C.teal} />
                        </div>
                    </div>

                    {/* Recent history */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            Recent Attendance (Last 10 Records)
                        </p>
                        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: C.border }}>
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr style={{ background: C.dark }}>
                                        {['Date', 'Status', 'Clock In', 'Clock Out', 'Hours', 'OT/UT', 'Proof'].map(h => (
                                            <th key={h} className="text-left py-2 px-3 font-semibold uppercase tracking-wide"
                                                style={{ color: C.muted, fontSize: 10 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedHistory.length === 0 && (
                                        <tr><td colSpan={7} className="py-6 text-center text-xs" style={{ color: C.muted }}>
                                            No records found.
                                        </td></tr>
                                    )}
                                    {sortedHistory.map(r => {
                                        const dur = r.clock_in && r.clock_out
                                            ? new Date(r.clock_out) - new Date(r.clock_in) : null
                                        return (
                                            <tr key={r.id} className="hover:bg-gray-50 transition-colors"
                                                style={{ borderTop: `1px solid ${C.border}` }}>
                                                <td className="py-2 px-3 font-medium" style={{ color: C.text }}>{fmtDate(r.date)}</td>
                                                <td className="py-2 px-3">
                                                    <StatusChip status={
                                                        r.clock_in
                                                            ? r.status                                    // clocked in → real status
                                                            : r.status === 'on_leave'
                                                                ? 'on_leave'                              // leave beats everything
                                                                : isDayOff(r.date, schedule)
                                                                    ? 'day_off'                           // day off beats absent/waiting
                                                                    : deriveTodayStatus(r, new Date(), schedule) ?? r.status
                                                    } />
                                                </td>
                                                <td className="py-2 px-3" style={{ color: C.blue }}>{fmtTime(r.clock_in)}</td>
                                                <td className="py-2 px-3" style={{ color: C.teal }}>{fmtTime(r.clock_out)}</td>
                                                <td className="py-2 px-3 font-semibold" style={{ color: C.green }}>{fmtDuration(dur)}</td>
                                                <td className="py-2 px-3"><OvertimeChip record={r} /></td>
                                                <td className="py-2 px-3">
                                                    <div className="flex items-center gap-1.5">
                                                        {r.clock_in_photo ? (
                                                            <button onClick={() => onViewPhoto?.({ url: r.clock_in_photo, label: 'Clock-In Photo' })} title="Clock-in photo">
                                                                <img src={r.clock_in_photo} alt="in"
                                                                    className="w-7 h-7 rounded object-cover border hover:opacity-75 transition-opacity"
                                                                    style={{ borderColor: C.green }} />
                                                            </button>
                                                        ) : (
                                                            <div className="w-7 h-7 rounded border flex items-center justify-center"
                                                                style={{ borderColor: C.border }}>
                                                                <span style={{ color: C.muted, fontSize: 8 }}>—</span>
                                                            </div>
                                                        )}
                                                        {r.clock_out_photo ? (
                                                            <button onClick={() => onViewPhoto?.({ url: r.clock_out_photo, label: 'Clock-Out Photo' })} title="Clock-out photo">
                                                                <img src={r.clock_out_photo} alt="out"
                                                                    className="w-7 h-7 rounded object-cover border hover:opacity-75 transition-opacity"
                                                                    style={{ borderColor: C.coral }} />
                                                            </button>
                                                        ) : (
                                                            <div className="w-7 h-7 rounded border flex items-center justify-center"
                                                                style={{ borderColor: C.border }}>
                                                                <span style={{ color: C.muted, fontSize: 8 }}>—</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Staff row card ─────────────────────────────────────────────────────────
function StaffRow({ staff, branchName, branchColorIdx, attendance, orders, pos, processed, schedule, onViewDetails, startBound, endBound }) {
    const today = getLocalDateStr()
    const todayRecord = attendance?.find(r => r.date === today)
    const branchColor = BRANCH_COLORS[branchColorIdx % BRANCH_COLORS.length]

    const windowAtt = useMemo(() =>
        filterByDate(attendance ?? [], 'date', startBound, endBound),
        [attendance, startBound, endBound])

    const present = windowAtt.filter(r => r.status === 'present').length
    const late = windowAtt.filter(r => r.status === 'late').length
    const absent = windowAtt.filter(r => r.status === 'absent').length
    const worked = present + late
    const rate = windowAtt.length ? Math.round((worked / windowAtt.length) * 100) : 0
    const totalMs = windowAtt
        .filter(r => r.clock_in && r.clock_out)
        .reduce((s, r) => s + (new Date(r.clock_out) - new Date(r.clock_in)), 0)

    const statusColor = staff.status === 'Active' ? C.green : C.muted

    return (
        <div className="rounded-2xl border p-4 hover:shadow-md transition-shadow cursor-pointer"
            style={{ background: C.card, borderColor: C.border }}
            onClick={onViewDetails}>

            {/* Top row */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Avatar firstName={staff.first_name} lastName={staff.last_name}
                        profilePic={staff.profile_picture_url} size={40} />
                    <div>
                        <p className="m-0 font-bold text-sm" style={{ color: C.text }}>
                            {staff.role === 'Barista' ? `${staff.first_name} ${staff.last_name}` : shortBranchName(branchName)}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-[11px]" style={{ color: C.muted }}>{staff.role}</span>
                            {staff.role === 'Barista' && (
                                <>
                                    <span className="w-1 h-1 rounded-full inline-block" style={{ background: C.border }} />
                                    {/* Branch badge — baristas only, admins already show branch as their name */}
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                                        style={{ background: branchColor + '22', color: branchColor }}>
                                        {shortBranchName(branchName)}
                                    </span>
                                </>
                            )}
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                                style={{ background: statusColor + '18', color: statusColor }}>
                                {staff.status}
                            </span>
                        </div>
                        {(staff.role === 'Barista' ? staff.email : staff.company_email) && (
                            <p className="m-0 text-[11px] mt-0.5 truncate" style={{ color: C.muted, maxWidth: 220 }}>
                                {staff.role === 'Barista' ? staff.email : staff.company_email}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                    {todayRecord ? (
                        <StatusChip status={
                            !todayRecord.clock_in
                                ? deriveTodayStatus(todayRecord, new Date(), schedule)
                                : todayRecord.status
                        } />
                    ) : (
                        <StatusChip status={isDayOff(today, schedule) ? 'day_off' : 'waiting'} />
                    )}
                    <span className="text-[10px] flex items-center gap-0.5" style={{ color: C.muted }}>
                        View Full
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </span>
                </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-5 gap-2">
                <div className="col-span-2 rounded-xl p-3 border" style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: C.muted }}>
                        Attendance
                    </p>
                    <div className="flex items-baseline gap-1 mb-1.5">
                        <span className="text-lg font-bold"
                            style={{ color: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }}>
                            {rate}%
                        </span>
                        <span className="text-[11px]" style={{ color: C.muted }}>
                            {windowAtt.length > 0 ? `${windowAtt.length} day${windowAtt.length !== 1 ? 's' : ''}` : 'no data'}
                        </span>
                    </div>
                    <div className="h-1.5 rounded-full mb-2" style={{ background: C.border }}>
                        <div className="h-1.5 rounded-full"
                            style={{ width: `${rate}%`, background: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }} />
                    </div>
                    <div className="flex gap-3">
                        <span className="text-[10px]" style={{ color: C.green }}>{present} present</span>
                        <span className="text-[10px]" style={{ color: C.amber }}>{late} late</span>
                        <span className="text-[10px]" style={{ color: C.coral }}>{absent} absent</span>
                    </div>
                </div>

                <div className="rounded-xl p-3 border flex flex-col justify-between"
                    style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.muted }}>Hours</p>
                    <p className="m-0 text-lg font-bold" style={{ color: C.blue }}>{fmtDuration(totalMs)}</p>
                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>worked</p>
                </div>

                <div className="rounded-xl p-3 border flex flex-col justify-between"
                    style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.muted }}>Processed</p>
                    <p className="m-0 text-lg font-bold" style={{ color: C.amber }}>{processed?.orderCount ?? 0}</p>
                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>{fmtCurrency(processed?.revenue ?? 0)}</p>
                </div>

                <div className="rounded-xl p-3 border flex flex-col justify-between"
                    style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.muted }}>POS</p>
                    <p className="m-0 text-lg font-bold" style={{ color: C.purple }}>{pos?.count ?? 0}</p>
                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>{fmtCurrency(pos?.revenue ?? 0)}</p>
                </div>
            </div>
        </div>
    )
}

export default function SuperAdminStaffAnalytics() {

    const [branches, setBranches] = useState([])
    const [staffList, setStaffList] = useState([])
    const [scheduleMap, setScheduleMap] = useState({})
    const [baristaList, setBaristaList] = useState([])
    const [rawBaristaAttendanceMap, setRawBaristaAttendanceMap] = useState({})
    const [rawBaristaOrders, setRawBaristaOrders] = useState([])
    const [baristaScheduleMap, setBaristaScheduleMap] = useState({})
    const [rawAttendanceMap, setRawAttendanceMap] = useState({})
    const [rawOrders, setRawOrders] = useState([])
    const [rawProcessedItems, setRawProcessedItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedStaff, setSelectedStaff] = useState(null)
    const [drawerAttendance, setDrawerAttendance] = useState([])
    const [viewPhoto, setViewPhoto] = useState(null)
    const [searchQ, setSearchQ] = useState('')
    const [filterStatus, setFilterStatus] = useState('all')
    const [filterBranch, setFilterBranch] = useState(null)
    const [filterRole, setFilterRole] = useState('all')
    const [sortBy, setSortBy] = useState('name')
    const [range, setRange] = useState('all')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [showBranchBreakdown, setShowBranchBreakdown] = useState(false)
    const [staffPage, setStaffPage] = useState(1)
    const [baristaPage, setBaristaPage] = useState(1)

    const { startBound, endBound } = useDateBounds(range, customStart, customEnd)

    const branchMap = useMemo(() => {
        const map = {}
        branches.forEach((b, i) => { map[b.branch_id] = { name: b.name, colorIdx: i } })
        return map
    }, [branches])

    const roleOptions = useMemo(() => {
        const set = new Set(staffList.map(s => s.role))
        if (baristaList.length > 0) set.add('Barista')
        return Array.from(set).sort()
    }, [staffList, baristaList])

    // Network Overview reacts to the branch filter — "All Branches" shows network-wide
    // totals, selecting a branch narrows the overview to just that branch's staff.
    const networkStaffList = useMemo(() =>
        filterBranch === null ? staffList : staffList.filter(s => s.branch_id === filterBranch),
        [staffList, filterBranch])

    const networkBaristaList = useMemo(() =>
        filterBranch === null ? baristaList : baristaList.filter(b => b.branch_id === filterBranch),
        [baristaList, filterBranch])

    useEffect(() => { fetchAll() }, [])

    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

    const fetchAll = async () => {
        setLoading(true)
        try {
            const res = await fetch(`${BASE_URL}/api/staff-analytics/overview`, { headers: authHeaders() })
            if (!res.ok) {
                const body = await res.text()
                console.error('fetchAll failed:', res.status, body)
                setLoading(false)
                return
            }
            const { branches, staff, attendance, schedules, orders, processedItems } = await res.json()

            setBranches(branches ?? [])

            if (!branches?.length || !staff?.length) {
                setStaffList([])
                setLoading(false)
                return
            }

            setStaffList(staff.filter(s => s.role !== 'Barista'))
            setScheduleMap(schedules ?? {})

            const staffIds = staff.map(s => s.id)
            const aMap = {}
            staffIds.forEach(id => { aMap[id] = [] })
                ; (attendance ?? []).forEach(r => { aMap[r.staff_id]?.push(r) })
            setRawAttendanceMap(aMap)

            setRawOrders(orders ?? [])
            setRawProcessedItems(processedItems ?? [])

            // ── Baristas: sourced from barista_info, not the staff table ──
            const branchIds = branches.map(b => b.branch_id)
            const { data: baristaData } = await supabase
                .from('barista_info')
                .select('barista_id, first_name, last_name, email, status, branch_id')
                .in('branch_id', branchIds)
                .eq('status', 'Active')

            const baristaIdsFetched = (baristaData ?? []).map(b => b.barista_id)
            setBaristaList(baristaData ?? [])

            if (baristaIdsFetched.length > 0) {
                const { data: baristaAttData } = await supabase
                    .from('staff_attendance')
                    .select('*')
                    .in('barista_id', baristaIdsFetched)
                    .order('date', { ascending: false })
                const bAttMap = {}
                baristaIdsFetched.forEach(id => { bAttMap[id] = [] })
                    ; (baristaAttData ?? []).forEach(r => { if (r.barista_id) bAttMap[r.barista_id]?.push(r) })
                setRawBaristaAttendanceMap(bAttMap)

                const { data: baristaSchedulesData } = await supabase
                    .from('staff_schedules')
                    .select('barista_id, work_days, default_scheduled_in, default_scheduled_out')
                    .in('barista_id', baristaIdsFetched)
                setBaristaScheduleMap(Object.fromEntries((baristaSchedulesData ?? []).map(s => [s.barista_id, s])))

                const { data: baristaOrdersData } = await supabase
                    .from('orders')
                    .select('order_id, branch_id, barista_id, pos_processed_by, order_completed_by, final_amount, order_source, created_at')
                    .in('branch_id', branchIds)
                    .eq('order_status', 'completed')
                    .or(`barista_id.in.(${baristaIdsFetched.join(',')}),pos_processed_by.in.(${baristaIdsFetched.join(',')}),order_completed_by.in.(${baristaIdsFetched.join(',')})`)
                    .order('created_at', { ascending: false })
                setRawBaristaOrders(baristaOrdersData ?? [])
            } else {
                setRawBaristaAttendanceMap({})
                setBaristaScheduleMap({})
                setRawBaristaOrders([])
            }
        } catch (err) {
            console.error('fetchAll error:', err)
        } finally {
            setLoading(false)
        }
    }

    const staffIds = useMemo(() => staffList.map(s => s.id), [staffList])

    const attendanceMap = useMemo(() => {
        const result = {}
        staffIds.forEach(id => {
            result[id] = filterByDate(rawAttendanceMap[id] ?? [], 'date', startBound, endBound)
        })
        return result
    }, [rawAttendanceMap, staffIds, startBound, endBound])

    const baristaIds = useMemo(() => baristaList.map(b => b.barista_id), [baristaList])

    const { baristaOrdersMap, baristaPosMap } = useMemo(() => {
        const filtered = filterByDate(rawBaristaOrders, 'created_at', startBound, endBound)
        const { prepMap, posMap } = buildBaristaOrderMaps(baristaIds, filtered)
        return { baristaOrdersMap: prepMap, baristaPosMap: posMap }
    }, [rawBaristaOrders, baristaIds, startBound, endBound])

    const { ordersMap, posMap, processedMap } = useMemo(() => {
        const filtered = filterByDate(rawOrders, 'created_at', startBound, endBound)
        const { oMap, pMap } = buildOrderMaps(staffIds, filtered)

        const prMap = {}
        staffIds.forEach(id => { prMap[id] = { count: 0, revenue: 0, seenOrderIds: new Set() } })

        const filteredProcessed = filterByDate(rawProcessedItems, 'orders.created_at', startBound, endBound)
        filteredProcessed.forEach(item => {
            if (!item.processed_by) return
            if (!prMap[item.processed_by]) return
            const entry = prMap[item.processed_by]
            entry.count += 1
            if (!entry.seenOrderIds.has(item.order_id)) {
                entry.seenOrderIds.add(item.order_id)
                entry.revenue += Number(item.orders?.final_amount ?? 0)
            }
        })

        staffIds.forEach(id => {
            prMap[id].orderCount = prMap[id].seenOrderIds.size
            delete prMap[id].seenOrderIds
        })

        return { ordersMap: oMap, posMap: pMap, processedMap: prMap }
    }, [rawOrders, rawProcessedItems, staffIds, startBound, endBound])

    const staffByBranch = useMemo(() => {
        const map = {}
        staffList.forEach(s => {
            if (!map[s.branch_id]) map[s.branch_id] = []
            map[s.branch_id].push(s)
        })
        return map
    }, [staffList])

    const baristaByBranch = useMemo(() => {
        const map = {}
        baristaList.forEach(b => {
            if (!map[b.branch_id]) map[b.branch_id] = []
            map[b.branch_id].push(b)
        })
        return map
    }, [baristaList])

    const openDrawer = async (staff) => {
        setSelectedStaff(staff)
        try {
            const res = await fetch(`${BASE_URL}/api/staff-analytics/attendance/${staff.id}`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch attendance')
            setDrawerAttendance(await res.json())
        } catch (err) {
            console.error('Error fetching staff attendance:', err)
            setDrawerAttendance([])
        }
    }

    const openBaristaDrawer = async (barista) => {
        setSelectedStaff({
            ...barista,
            id: barista.barista_id,
            role: 'Barista',
            employment_type: barista.employment_type ?? '',
            profile_picture_url: null,
            branch_id: barista.branch_id,
        })
        const { data } = await supabase
            .from('staff_attendance')
            .select('*')
            .eq('barista_id', barista.barista_id)
            .order('date', { ascending: false })
        setDrawerAttendance(data ?? [])
    }

    const filtered = useMemo(() => staffList.filter(s => {
        const branchName = branchMap[s.branch_id]?.name ?? ''
        const matchSearch = searchQ === '' ||
            `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchQ.toLowerCase()) ||
            s.role.toLowerCase().includes(searchQ.toLowerCase()) ||
            branchName.toLowerCase().includes(searchQ.toLowerCase())
        const matchStatus = filterStatus === 'all' || s.status === filterStatus
        const matchBranch = filterBranch === null || s.branch_id === filterBranch
        const matchRole = filterRole === 'all' || s.role === filterRole
        return matchSearch && matchStatus && matchBranch && matchRole
    }), [staffList, searchQ, filterStatus, filterBranch, filterRole, branchMap])

    const sorted = useMemo(() => [...filtered].sort((a, b) => {
        if (sortBy === 'name') return a.first_name.localeCompare(b.first_name)
        if (sortBy === 'branch') return (branchMap[a.branch_id]?.name ?? '').localeCompare(branchMap[b.branch_id]?.name ?? '')
        if (sortBy === 'attendance') {
            const rate = s => {
                const m = attendanceMap[s.id] ?? []
                const worked = m.filter(r => r.status === 'present' || r.status === 'late').length
                return m.length ? worked / m.length : 0
            }
            return rate(b) - rate(a)
        }
        if (sortBy === 'orders') return (ordersMap[b.id]?.count ?? 0) - (ordersMap[a.id]?.count ?? 0)
        if (sortBy === 'pos') return (posMap[b.id]?.count ?? 0) - (posMap[a.id]?.count ?? 0)
        return 0
    }), [filtered, sortBy, attendanceMap, ordersMap, posMap, branchMap])

    // Baristas weren't previously affected by search/branch/status filters — bringing them in line with staff
    const filteredBaristaList = useMemo(() => baristaList.filter(b => {
        const branchName = branchMap[b.branch_id]?.name ?? ''
        const matchSearch = searchQ === '' ||
            `${b.first_name} ${b.last_name}`.toLowerCase().includes(searchQ.toLowerCase()) ||
            branchName.toLowerCase().includes(searchQ.toLowerCase())
        const matchStatus = filterStatus === 'all' || b.status === filterStatus
        const matchBranch = filterBranch === null || b.branch_id === filterBranch
        const matchRole = filterRole === 'all' || filterRole === 'Barista'
        return matchSearch && matchStatus && matchBranch && matchRole
    }), [baristaList, searchQ, filterStatus, filterBranch, filterRole, branchMap])

    // Reset both pagers whenever the underlying filtered sets change
    useEffect(() => {
        setStaffPage(1)
        setBaristaPage(1)
    }, [searchQ, filterStatus, filterBranch, filterRole, sortBy, range, customStart, customEnd])

    const staffTotalPages = Math.max(1, Math.ceil(sorted.length / STAFF_PAGE_SIZE))
    const paginatedStaff = sorted.slice((staffPage - 1) * STAFF_PAGE_SIZE, staffPage * STAFF_PAGE_SIZE)

    const baristaTotalPages = Math.max(1, Math.ceil(filteredBaristaList.length / STAFF_PAGE_SIZE))
    const paginatedBaristas = filteredBaristaList.slice((baristaPage - 1) * STAFF_PAGE_SIZE, baristaPage * STAFF_PAGE_SIZE)

    const handleNetworkOverview = () => {
        setFilterBranch(null)
        setShowBranchBreakdown(true)
        setTimeout(() => {
            document.getElementById('network-overview-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 50)
    }

    if (loading) return (
        <div className="p-6">
            <Card><Spinner height={300} /></Card>
        </div>
    )

    return (
        <div className="p-6 font-sans flex flex-col gap-5">
            {viewPhoto && (
                <PhotoModal url={viewPhoto.url} label={viewPhoto.label} onClose={() => setViewPhoto(null)} />
            )}

            {/* Drawer */}
            {selectedStaff && (
                <StaffDrawer
                    staff={selectedStaff}
                    branchName={branchMap[selectedStaff.branch_id]?.name ?? '—'}
                    schedule={selectedStaff.role === 'Barista' ? baristaScheduleMap[selectedStaff.id] : scheduleMap[selectedStaff.id] ?? null}
                    attendance={drawerAttendance}
                    orders={selectedStaff.role === 'Barista' ? baristaOrdersMap[selectedStaff.id] : ordersMap[selectedStaff.id]}
                    pos={selectedStaff.role === 'Barista' ? baristaPosMap[selectedStaff.id] : posMap[selectedStaff.id]}
                    processed={selectedStaff.role === 'Barista' ? undefined : processedMap?.[selectedStaff.id]}
                    isBarista={selectedStaff.role === 'Barista'}
                    onClose={() => setSelectedStaff(null)}
                    onLeaveDeleted={(deletedId) => {
                        setDrawerAttendance(prev => prev.filter(r => r.id !== deletedId))
                    }}
                    startBound={startBound}
                    endBound={endBound}
                    onViewPhoto={setViewPhoto}
                />
            )}

            {/* Network summary — filters by the branch pill selected below */}
            {(staffList.length > 0 || baristaList.length > 0) && (
                <div id="network-overview-card">
                    <NetworkSummaryBar
                        staffList={networkStaffList}
                        baristaList={networkBaristaList}
                        branchMap={branchMap}
                        rawAttendanceMap={rawAttendanceMap}
                        rawBaristaAttendanceMap={rawBaristaAttendanceMap}
                        ordersMap={ordersMap}
                        baristaOrdersMap={baristaOrdersMap}
                        posMap={posMap}
                        baristaPosMap={baristaPosMap}
                        processedMap={processedMap}
                        startBound={startBound}
                        endBound={endBound}
                        selectedBranchName={filterBranch !== null ? shortBranchName(branchMap[filterBranch]?.name) : null}
                    />
                </div>
            )}

            {/* Per-branch summary breakdown */}
            {branches.length > 0 && (
                <Card>
                    {/* Clickable header */}
                    <button
                        onClick={() => setShowBranchBreakdown(p => !p)}
                        className="w-full flex items-center justify-between"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                        <SectionTitle
                            title="Per-Branch Breakdown"
                            subtitle={periodLabel(startBound, endBound)}
                            className="mb-0 text-left"
                        />
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18" height="18" viewBox="0 0 24 24"
                            className="flex-shrink-0 transition-transform duration-200"
                            style={{
                                transform: showBranchBreakdown ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: C.muted,
                                marginTop: -12
                            }}
                        >
                            <path fill="currentColor" d="M7 10l5 5l5-5z" />
                        </svg>
                    </button>

                    {/* Collapsible content */}
                    {showBranchBreakdown && (
                        <div className="mt-4">
                            <BranchBreakdownTable
                                branches={branches}
                                staffByBranch={staffByBranch}
                                baristaByBranch={baristaByBranch}
                                rawAttendanceMap={rawAttendanceMap}
                                rawBaristaAttendanceMap={rawBaristaAttendanceMap}
                                processedMap={processedMap}
                                baristaOrdersMap={baristaOrdersMap}
                            />
                        </div>
                    )}
                </Card>
            )}

            {/* Filters bar */}
            <Card>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[200px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                                placeholder="Search by name or role…"
                                className="w-full pl-9 pr-4 py-2 rounded-xl border text-sm outline-none"
                                style={{ borderColor: C.border, color: C.text, background: C.dark }} />
                        </div>

                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                            className="px-3 py-2 rounded-xl border text-sm outline-none"
                            style={{ borderColor: C.border, color: C.text, background: C.dark }}>
                            <option value="all">All statuses</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>

                        <RoleFilterDropdown
                            roles={roleOptions}
                            selectedRole={filterRole}
                            setSelectedRole={setFilterRole}
                        />

                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                            className="px-3 py-2 rounded-xl border text-sm outline-none"
                            style={{ borderColor: C.border, color: C.text, background: C.dark }}>
                            <option value="name">Sort: Name</option>
                            <option value="branch">Sort: Branch</option>
                            <option value="attendance">Sort: Attendance</option>
                            <option value="orders">Sort: Orders</option>
                            <option value="pos">Sort: POS Transaction</option>
                        </select>

                        <span className="text-xs" style={{ color: C.muted }}>
                            {sorted.length} staff member{sorted.length !== 1 ? 's' : ''}
                        </span>

                        <button onClick={fetchAll}
                            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-colors hover:bg-gray-100"
                            style={{ borderColor: C.border, color: C.textSoft }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10" />
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                            </svg>
                            Refresh
                        </button>
                    </div>

                    {/* Branch filter pills */}
                    <div className="pt-2 border-t" style={{ borderColor: C.border }}>
                        <BranchFilter
                            branches={branches}
                            selectedBranch={filterBranch}
                            setSelectedBranch={setFilterBranch}
                        />
                    </div>

                    {/* Date range */}
                    <div className="flex items-start justify-between flex-wrap gap-2 pt-3 border-t"
                        style={{ borderColor: C.border }}>
                        <div>
                            <p className="m-0 text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted }}>
                                Date Range
                            </p>
                            <p className="m-0 text-[11px] mt-0.5" style={{ color: C.textSoft }}>
                                Filters attendance and order stats across all cards
                            </p>
                        </div>
                        <RangeSelector
                            range={range} setRange={setRange}
                            customStart={customStart} setCustomStart={setCustomStart}
                            customEnd={customEnd} setCustomEnd={setCustomEnd}
                        />
                    </div>
                </div>
            </Card>

            {/* Staff cards */}
            {sorted.length === 0 ? (
                <Card>
                    <div className="py-16 flex flex-col items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                            fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        <p className="text-sm font-medium" style={{ color: C.muted }}>
                            {staffList.length === 0 ? 'No staff found.' : 'No staff match your filters.'}
                        </p>
                    </div>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {paginatedStaff.map(s => (
                            <StaffRow
                                key={s.id}
                                staff={s}
                                branchName={branchMap[s.branch_id]?.name ?? '—'}
                                branchColorIdx={branchMap[s.branch_id]?.colorIdx ?? 0}
                                schedule={scheduleMap[s.id] ?? null}
                                attendance={rawAttendanceMap[s.id] ?? []}
                                orders={ordersMap[s.id]}
                                pos={posMap[s.id]}
                                processed={processedMap[s.id]}
                                onViewDetails={() => openDrawer(s)}
                                startBound={startBound}
                                endBound={endBound}
                            />
                        ))}
                    </div>
                    {staffTotalPages > 1 && (
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-[11px]" style={{ color: C.muted }}>
                                Page {staffPage} of {staffTotalPages} · {sorted.length} admin{sorted.length !== 1 ? 's' : ''}
                            </span>
                            <div className="flex gap-1.5">
                                <button disabled={staffPage === 1} onClick={() => setStaffPage(p => p - 1)}
                                    className="px-3 py-1 rounded-lg text-[11px] border disabled:opacity-40"
                                    style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: 'pointer' }}>← Prev</button>
                                <button disabled={staffPage >= staffTotalPages} onClick={() => setStaffPage(p => p + 1)}
                                    className="px-3 py-1 rounded-lg text-[11px] border disabled:opacity-40"
                                    style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: 'pointer' }}>Next →</button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Barista Section */}
            {baristaList.length > 0 && (
                <>
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.muted }}>
                        Baristas ({filteredBaristaList.length})
                    </p>
                    {filteredBaristaList.length === 0 ? (
                        <Card>
                            <div className="py-10 flex flex-col items-center gap-2">
                                <p className="text-sm font-medium" style={{ color: C.muted }}>
                                    No baristas match your filters.
                                </p>
                            </div>
                        </Card>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                {paginatedBaristas.map(b => (
                                    <StaffRow
                                        key={b.barista_id}
                                        staff={{
                                            id: b.barista_id, first_name: b.first_name, last_name: b.last_name,
                                            role: 'Barista', status: b.status, employment_type: b.employment_type ?? '',
                                            profile_picture_url: null, branch_id: b.branch_id, email: b.email,
                                        }}
                                        branchName={branchMap[b.branch_id]?.name ?? '—'}
                                        branchColorIdx={branchMap[b.branch_id]?.colorIdx ?? 0}
                                        attendance={rawBaristaAttendanceMap[b.barista_id] ?? []}
                                        schedule={baristaScheduleMap[b.barista_id] ?? null}
                                        orders={baristaOrdersMap[b.barista_id]}
                                        pos={baristaPosMap[b.barista_id]}
                                        processed={undefined}
                                        onViewDetails={() => openBaristaDrawer(b)}
                                        startBound={startBound}
                                        endBound={endBound}
                                    />
                                ))}
                            </div>
                            {baristaTotalPages > 1 && (
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-[11px]" style={{ color: C.muted }}>
                                        Page {baristaPage} of {baristaTotalPages} · {filteredBaristaList.length} barista{filteredBaristaList.length !== 1 ? 's' : ''}
                                    </span>
                                    <div className="flex gap-1.5">
                                        <button disabled={baristaPage === 1} onClick={() => setBaristaPage(p => p - 1)}
                                            className="px-3 py-1 rounded-lg text-[11px] border disabled:opacity-40"
                                            style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: 'pointer' }}>← Prev</button>
                                        <button disabled={baristaPage >= baristaTotalPages} onClick={() => setBaristaPage(p => p + 1)}
                                            className="px-3 py-1 rounded-lg text-[11px] border disabled:opacity-40"
                                            style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: 'pointer' }}>Next →</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}